import type { AppointmentPlanningWorkOrderOverride } from "../../../_logic/appointments-contract"
import type { AppointmentPlanningManualAppointmentPayload } from "../../../../../../api/kehrblatt/planning"
import type { PlanningPreviewRow, PlanningPreviewRowChange } from "../_shared/_logic/table-contract"
import { projectAppointmentDuration } from "./appointmentProjection"
import { formatDateOnly } from "../../../_logic/dates"
import type { AppointmentPlanningStateCommand } from "./contract"
import {
  appointmentMeetsStartBound,
  PLANNING_START_BOUND_MESSAGE,
  type AppointmentPlanningStartBound,
} from "../_shared/_logic/start-bound"

export type AppointmentPlanningRowChangeResult = {
  error?: string
  override: AppointmentPlanningWorkOrderOverride | null
  commands: AppointmentPlanningStateCommand[]
}

const manualFlexDateFromAppointment = (
  appointment?: AppointmentPlanningManualAppointmentPayload | null,
): string | null =>
  formatDateOnly(appointment?.timeframe_start_at ?? appointment?.start_at ?? null) || null

const allowedAppointmentEmployeeId = (
  row: PlanningPreviewRow,
  value?: string | null,
): string | null => {
  const employeeId = String(value ?? "").trim()

  if (!employeeId) return null

  const allowed = new Set(row.allowedEmployeeIds.map(String).filter(Boolean))

  return !allowed.size || allowed.has(employeeId) ? employeeId : null
}

const currentAppointmentEmployeeId = (
  row: PlanningPreviewRow,
  override: AppointmentPlanningWorkOrderOverride,
): string | null =>
  [
    override.appointment_employee_id,
    override.manual_appointment?.employee_id,
    override.manual_flex_appointment?.employee_id,
    row.appointmentEmployeeId,
  ]
    .map((value) => allowedAppointmentEmployeeId(row, value))
    .find((value): value is string => Boolean(value)) ?? null

const manualAppointmentForType = (
  row: PlanningPreviewRow,
  override: AppointmentPlanningWorkOrderOverride,
  type: "HARD" | "FLEX",
): AppointmentPlanningManualAppointmentPayload | null => {
  const employeeId = currentAppointmentEmployeeId(row, override)
  const source = override.manual_appointment ?? override.manual_flex_appointment
  const startAt = source?.start_at ?? row.appointmentStartAt
  const endAt = source?.end_at ?? row.appointmentEndAt

  if (!employeeId || !startAt || !endAt) return null

  if (type === "FLEX") {
    return {
      start_at: startAt,
      end_at: endAt,
      employee_id: employeeId,
      flex_type: "SOFT",
      timeframe_start_at: source?.timeframe_start_at ?? row.appointmentTimeframeStartAt ?? startAt,
      timeframe_end_at: source?.timeframe_end_at ?? row.appointmentTimeframeEndAt ?? endAt,
    }
  }

  return {
    start_at: startAt,
    end_at: endAt,
    employee_id: employeeId,
    flex_type: "HARD",
    timeframe_start_at: null,
    timeframe_end_at: null,
  }
}

const shouldLoadManualFlexSlots = (
  row: PlanningPreviewRow,
  override: AppointmentPlanningWorkOrderOverride,
): boolean =>
  override.planning_strategy === "MANUAL" &&
  override.manual_appointment_type === "FLEX" &&
  Boolean(currentAppointmentEmployeeId(row, override)) &&
  Boolean(override.manual_flex_date)

const commandsForOverride = (
  row: PlanningPreviewRow,
  override: AppointmentPlanningWorkOrderOverride | null,
): AppointmentPlanningStateCommand[] =>
  override && shouldLoadManualFlexSlots(row, override)
    ? [{ kind: "load_manual_flex_slots", rowId: row.id }]
    : []

const withCommands = (
  row: PlanningPreviewRow,
  override: AppointmentPlanningWorkOrderOverride | null,
): AppointmentPlanningRowChangeResult => ({
  override,
  commands: commandsForOverride(row, override),
})

const manualAppointmentFromFlexSlot = (
  value: Extract<PlanningPreviewRowChange, { kind: "manual_flex_slot" }>["value"],
): AppointmentPlanningManualAppointmentPayload | null => {
  if (!value) return null

  return {
    start_at: value.appointment_start_at,
    end_at: value.appointment_end_at,
    employee_id: value.employee_id,
    flex_type: "SOFT",
    timeframe_start_at: value.timeframe_start_at,
    timeframe_end_at: value.timeframe_end_at,
  }
}

const projectPlanningRowChange = ({
  row,
  change,
  current,
}: {
  row: PlanningPreviewRow
  change: PlanningPreviewRowChange
  current: AppointmentPlanningWorkOrderOverride
}): AppointmentPlanningRowChangeResult => {
  if (change.kind === "appointment_employee") {
    const allowed = new Set(row.allowedEmployeeIds.map(String).filter(Boolean))
    const nextEmployeeId = String(change.value ?? "").trim()
    const employeeId =
      nextEmployeeId && (!allowed.size || allowed.has(nextEmployeeId)) ? nextEmployeeId : null
    const appointment = employeeId
      ? manualAppointmentForType(
          { ...row, appointmentEmployeeId: employeeId },
          { ...current, appointment_employee_id: employeeId },
          row.appointmentTypeValue,
        )
      : null
    const manualAppointment =
      appointment && employeeId ? { ...appointment, employee_id: employeeId } : null

    return withCommands(row, {
      ...current,
      appointment_employee_id: employeeId,
      manual_appointment: manualAppointment,
      manual_flex_appointment: manualAppointment?.flex_type === "SOFT" ? manualAppointment : null,
      manual_flex_slot_id: null,
      ...(appointment
        ? {
            planning_strategy: "MANUAL",
            manual_appointment_type: row.appointmentTypeValue,
          }
        : {}),
    })
  }

  if (change.kind === "planning_strategy") {
    if (change.value === "MANUAL") {
      const appointmentType = current.manual_appointment_type ?? row.appointmentTypeValue ?? "HARD"
      const manualAppointment = manualAppointmentForType(row, current, appointmentType)
      const manualFlexDate =
        manualFlexDateFromAppointment(manualAppointment) ?? current.manual_flex_date ?? null

      return withCommands(row, {
        ...current,
        appointment_employee_id: currentAppointmentEmployeeId(row, current),
        planning_strategy: "MANUAL",
        manual_appointment_type: appointmentType,
        manual_appointment: manualAppointment,
        manual_flex_date: appointmentType === "FLEX" ? manualFlexDate : null,
        manual_flex_slot_id: null,
        manual_flex_appointment: appointmentType === "FLEX" ? manualAppointment : null,
      })
    }

    return withCommands(row, {
      ...current,
      planning_strategy: change.value,
      manual_appointment_type: null,
      manual_appointment: null,
      manual_flex_date: null,
      manual_flex_slot_id: null,
      manual_flex_appointment: null,
    })
  }

  if (change.kind === "manual_appointment_type") {
    const employeeId = currentAppointmentEmployeeId(row, current)

    if (change.value === "FLEX") {
      const manualAppointment = manualAppointmentForType(row, current, "FLEX")
      const seedDate =
        manualFlexDateFromAppointment(manualAppointment) ??
        current.manual_flex_date ??
        formatDateOnly(row.appointmentStartAt)

      return withCommands(row, {
        ...current,
        appointment_employee_id: employeeId,
        planning_strategy: "MANUAL",
        manual_appointment_type: "FLEX",
        manual_appointment: manualAppointment,
        manual_flex_date: seedDate || current.manual_flex_date || null,
        manual_flex_slot_id: null,
        manual_flex_appointment: manualAppointment,
      })
    }

    const manualAppointment = manualAppointmentForType(row, current, "HARD")

    return withCommands(row, {
      ...current,
      appointment_employee_id: employeeId,
      planning_strategy: "MANUAL",
      manual_appointment_type: "HARD",
      manual_appointment: manualAppointment,
      manual_flex_date: null,
      manual_flex_slot_id: null,
      manual_flex_appointment: null,
    })
  }

  if (change.kind === "manual_appointment") {
    const flexDate = manualFlexDateFromAppointment(change.value)

    return withCommands(row, {
      ...current,
      planning_strategy: "MANUAL",
      manual_appointment_type:
        change.value?.flex_type === "SOFT" ? "FLEX" : (current.manual_appointment_type ?? "HARD"),
      appointment_employee_id:
        allowedAppointmentEmployeeId(row, change.value?.employee_id) ??
        currentAppointmentEmployeeId(row, current),
      manual_appointment: change.value,
      manual_flex_date: change.value?.flex_type === "SOFT" ? flexDate : null,
      manual_flex_slot_id: null,
      manual_flex_appointment: change.value?.flex_type === "SOFT" ? change.value : null,
    })
  }

  if (change.kind === "manual_flex_date") {
    return withCommands(row, {
      ...current,
      appointment_employee_id: currentAppointmentEmployeeId(row, current),
      planning_strategy: "MANUAL",
      manual_appointment_type: "FLEX",
      manual_flex_date: formatDateOnly(change.value) || null,
      manual_flex_slot_id: null,
      manual_flex_appointment: null,
      manual_appointment: null,
    })
  }

  if (change.kind === "manual_flex_slot") {
    const manualAppointment = manualAppointmentFromFlexSlot(change.value)

    return withCommands(row, {
      ...current,
      planning_strategy: "MANUAL",
      manual_appointment_type: "FLEX",
      appointment_employee_id:
        allowedAppointmentEmployeeId(row, change.value?.employee_id) ??
        currentAppointmentEmployeeId(row, current),
      manual_flex_date: change.value
        ? formatDateOnly(change.value.timeframe_start_at)
        : (current.manual_flex_date ?? null),
      manual_flex_slot_id: change.value?.slot_id ?? null,
      manual_flex_appointment: manualAppointment,
      manual_appointment: manualAppointment,
    })
  }

  return withCommands(row, null)
}

export const applyPlanningRowChange = (
  input: Parameters<typeof projectPlanningRowChange>[0] & {
    startBound: AppointmentPlanningStartBound
  },
): AppointmentPlanningRowChangeResult => {
  const result = projectPlanningRowChange(input)
  const override = result.override

  if (!override || override.planning_strategy === "KEEP_EXISTING") return result

  if (
    !appointmentMeetsStartBound(override.manual_appointment, input.startBound) ||
    !appointmentMeetsStartBound(override.manual_flex_appointment, input.startBound) ||
    (override.manual_flex_date && override.manual_flex_date < input.startBound.startDate)
  )
    return { override: null, commands: [], error: PLANNING_START_BOUND_MESSAGE }

  return result
}

export const nextAppointmentPlanningRowOverride = (
  input: Parameters<typeof applyPlanningRowChange>[0],
): AppointmentPlanningWorkOrderOverride | null => applyPlanningRowChange(input).override

export const buildPreviewRowRemovalState = ({
  row,
  workOrderOverridesByRowId,
  excludedRowIds,
  dirtyRowIds,
}: {
  row: PlanningPreviewRow
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  excludedRowIds: string[]
  dirtyRowIds: Record<string, true>
}) => {
  const nextOverrides = { ...workOrderOverridesByRowId }
  delete nextOverrides[row.id]
  const nextDirtyRowIds = { ...dirtyRowIds }
  delete nextDirtyRowIds[row.id]

  return {
    workOrderOverridesByRowId: nextOverrides,
    excludedRowIds: Array.from(new Set([...excludedRowIds, row.id])),
    dirtyRowIds: nextDirtyRowIds,
  }
}

export const buildPreviewRowsRemovalState = ({
  rows,
  workOrderOverridesByRowId,
  excludedRowIds,
  dirtyRowIds,
}: {
  rows: PlanningPreviewRow[]
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  excludedRowIds: string[]
  dirtyRowIds: Record<string, true>
}) => {
  const uniqueRows = Array.from(
    new Map(rows.filter((row) => Boolean(row.id)).map((row) => [row.id, row])).values(),
  )
  let nextState = {
    workOrderOverridesByRowId,
    excludedRowIds,
    dirtyRowIds,
  }

  for (const row of uniqueRows) {
    if (nextState.excludedRowIds.includes(row.id)) continue

    const rowState = buildPreviewRowRemovalState({
      row,
      workOrderOverridesByRowId: nextState.workOrderOverridesByRowId,
      excludedRowIds: nextState.excludedRowIds,
      dirtyRowIds: nextState.dirtyRowIds,
    })
    nextState = {
      ...rowState,
    }
  }

  return nextState
}
